import { TTagModule, International } from "optimus-front/lib/services/playground";
import {
	LoadPoolProps,
	GetProductsResp,
	TProductElement as TFProductElement,
	TProductModule,
	PoolExtraInfo,
} from "optimus-front/lib/services/product";
import {
	TProductModule as ServerProductModule,
	Ui,
	Shape,
	TProductElement,
} from "services/page/admin";
import { ExtraInfo, FrontMeta } from "services/pool/app";
import { GetProducts } from "services/page/app";
import Constant from "utils/isomorphismConstant";
import { genPrmLink } from "utils/url";
import { isNotBlankArray } from "utils/array";
import { PrmParams } from "../prm";
import { transformProductPrice } from "utils/price";
import { ProductShape } from "views/Mobile/constants";
import { GetAutoLink } from "utils/browser/litbapp";
import { transformSign } from "utils/md5";
import { RuntimeProps } from "../../views/index";
import { transformMetaForApi } from "./index";
import { ParseProductTagFromUrlResp, heightLightTabIndex } from "./url";

// 商品图片占位图
export const placeholderGroup = (shape: Shape) => {
	if (shape === Shape.Square) {
		return "https://i.ezbuy.sg/Fgu47zjp9ABUt-ZQMKa7J-7M-aoW";
	}
	return "https://i.ezbuy.sg/FinkFHyvFkDe4HOIJ2zEhe9-YgVM";
};

export const transformProductModule = (
	items: ServerProductModule[],
	prm: PrmParams,
	ui: Ui,
): {
	tagGroup: TTagModule[];
	activeTab: string;
	nextItems: TProductModule[];
} => {
	if (!isNotBlankArray(items)) {
		return null;
	}
	let nextItems = [...items].map((item) => {
		const { productElements } = item;
		if (productElements) {
			return {
				...item,
				productElements: productElements.map((pElement, pIndex) => {
					return transformProduct(pElement, ui, { ...prm, position: pIndex });
				}),
			};
		}
		return item;
	}) as TProductModule[];

	const { tagGroup, activeTab } = transformProductTag(items, ui);
	return {
		tagGroup,
		activeTab,
		nextItems,
	};
};

function transformProduct(pElement: TProductElement, ui: Ui, prm: PrmParams) {
	const { floor, position, platform } = prm;
	const nextLink = genPrmLink(
		{
			floor,
			position: position + 1,
		},
		pElement.url,
	);
	const imgFromShape = pElement.extra.img;
	// 兼容后台管理配置值的历史错误值（后台选择 square，上报的value = rectA， rectA=ReacB）
	let img = [Shape.Square, Shape.RectangleA].includes(ui.shape)
		? imgFromShape.square
		: imgFromShape.rectangleA;
	const extra = transformProductExtra(pElement);
	img = img || pElement.img;
	return {
		...pElement,
		url: GetAutoLink(nextLink),
		img,
		price: transformProductPrice(platform, pElement.price),
		originPrice: transformProductPrice(platform, pElement.originPrice, true),
		extra,
	};
}

function transformProductExtra(product: TProductElement): PoolExtraInfo {
	const { discountInfo, tag, rightView, titleIcons } = product.extra || ({} as ExtraInfo);

	return {
		imgOnTopRight: { img: tag?.img },
		imgOnBottomLeft: { img: titleIcons?.[0]?.icon },
		imgOnTopLeft: { img: discountInfo?.imgUrl },
		collect: {
			text: rightView?.text,
		},
	};
}
function transformProductTag(items: ServerProductModule[], ui: Ui) {
	let activeTab = "";
	const result = items.reduce((res, item, index) => {
		const { tabExpand, name, poolId, productElements, ig } = item;

		if (!activeTab && productElements) {
			activeTab = `${poolId}`;
		}
		const textFocusColor = getColorFromTable(ui, "textFocusColor", "#fff");
		const bgColor = ui?.bgColor || "#fff";
		const tagFocusBg = getColorFromTable(ui, "tagFocusBg", "#ff5a00");
		const textColor = getColorFromTable(ui, "texColor", "#333");
		const igGroup: International = ig?.igroup?.[Constant.language];
		const igName = igGroup?.text;
		return [
			...res,
			{
				id: poolId,
				positionId: `${poolId}|${index}`,
				tabExpand,
				bgColor,
				focusBgColor: tagFocusBg,
				text: {
					content: igName || name,
					color: textColor,
					lightColor: textFocusColor,
				},
			},
		];
	}, []);
	return {
		activeTab,
		tagGroup: result.length > 1 ? result : [],
	};
}

export const fetchProductHeihgtLightPool = (
	itemPostion: string,
	tagQuery: ParseProductTagFromUrlResp,
	items: TProductModule[],
) => {
	let activeTabIndex = 0;
	if (itemPostion === tagQuery.modId) {
		activeTabIndex = heightLightTabIndex(items, tagQuery.tagName) || 0;
	}
	return items[activeTabIndex].poolId;
};

const getColorFromTable = (source: Ui, key: string, defaultColor: string = "") => {
	return source?.colorTable?.[key] ? source.colorTable[key] : defaultColor;
};

/**
 * 商品card poster的高宽比
 */
export const fetchProductPosterRadio = (shape: Shape): number => {
	if (shape === Shape.RectangleB) {
		return 1.25;
	}
	return 1;
};
/**
 * front 对加载商品的回调事件
 */
export const loadPoolByFront = ({
	ui,
	prm,
	runtime,
}: {
	ui: Ui;
	prm: PrmParams;
	runtime: RuntimeProps;
}) => async (props: LoadPoolProps): Promise<GetProductsResp> => {
	const { poolId, limit, offset, prmPosition } = props;
	let metaData: FrontMeta = transformMetaForApi(runtime);
	if (runtime.appKey) {
		metaData = {
			...metaData,
			userInfos: {
				app_key: runtime.appKey,
				app_secret: runtime.appSecret,
				sid: runtime.sid,
				sessionkey: runtime.sessionKey,
				sign: transformSign(runtime),
			},
		};
	}
	const pool = await GetProducts(
		{
			poolId,
			offset: offset || "0",
			limit,
			meta: {
				...metaData,
			},
		},
		{ baseInfo: { cookie: runtime.customerCookies, isuat: runtime.isUat } },
	).catch((error) => {
		console.error(error);
	});
	if (pool) {
		const nextProducts = pool.products.map((pElement: TProductElement, pIndex: number) => {
			return transformProduct(pElement, ui, { ...prm, position: pIndex + prmPosition });
		}) as TFProductElement[];

		/* 查看front 商品池请求信息 */
		return {
			hasNext: !!pool.hasNext,
			products: nextProducts,
		};
	}
	return {
		hasNext: false,
		products: [],
	};
};

/**
 * M productcard style
 */
export interface ProductCardStyleProps {
	shape?: Shape;
	col: number;
}
export const fetchMProductCardStyle = (props: ProductCardStyleProps) => {
	if (props.col === 1) {
		return {
			height: "6.5rem",
			width: "6.5rem",
		};
	}
	if (props.shape) {
		return ProductShape[props.shape];
	}
	return {};
};
/** col = 1 的css */
export const fetchProductCardStyle = (columnum: number): React.CSSProperties => {
	if (columnum === 1) {
		return {
			background: "#fff",
			padding: "0.625rem",
			display: "flex",
			borderRadius: "0.375rem",
			width: "calc(100vw - 1rem)",
			boxSizing: "border-box",
		};
	}
	return {};
};
export const fetchProductCardPosterStyle = (
	columnum: number,
	shapeType: Shape,
): React.CSSProperties => {
	if (columnum === 1) {
		return {
			position: "relative",
			width: "6.5rem",
			height: "6.5rem",
			flex: "0 0 auto",
			borderRadius: "3px",
			overflow: "hidden",
		};
	}
	return ProductShape[shapeType];
};
